import { Fix64 } from "../maths/fix64";
import { affixedSlow } from "./affixes";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import type { PetSystem } from "./petSystem";

/**
 * Howl (Pets v2, docs/PETS.md): once a wave (each stage that starts, a stage joining a running wave included, arms it
 * again), the first tick a ground raider is within `reach` of the flag's cell, every ground raider within reach is
 * stunned `stunTicks` — a boss (rares, the wild pet) `bossStunTicks`: Howl is the one stun a boss feels, at half. As
 * every stun: it stands still and strikes no one, Frostbound halves it, a longer stun already on it is kept, and
 * immunity to slows does not stop it. Flyers and Burrowers underground are never in reach.
 */
export function tickHowl(sim: LevelSim, pet: PetSystem): void {
  const howl = pet.rule.howl;
  if (!howl || pet.howlStage >= sim.stage) return;
  const r = sim.troops.rally, fx = Fix64.fromInt(r.x), fy = Fix64.fromInt(r.y), r2 = howl.reach.mul(howl.reach);
  let any = false;
  for (const e of sim.enemySlots) if (inReach(sim, e, fx, fy, r2)) { any = true; break; }
  if (!any) return;
  pet.howlStage = sim.stage;
  let stunned = 0;
  for (const e of sim.enemySlots) {
    if (!inReach(sim, e, fx, fy, r2)) continue;
    const ticks = affixedSlow(sim, e, Fix64.fromInt(e.isBoss && !e.isRare ? howl.bossStunTicks : howl.stunTicks)).floorToInt();
    if (ticks <= e.stunTicksLeft) continue;
    e.stunTicksLeft = ticks;
    stunned++;
    sim.events.add(SimEventKind.EnemyStunned, e.id, 0, ticks);
  }
  sim.events.add(SimEventKind.PetHowled, stunned, howl.stunTicks, sim.stage);
}

/** A ground raider the towers can target, within √r2 of (fx, fy). */
function inReach(sim: LevelSim, e: Enemy, fx: Fix64, fy: Fix64, r2: Fix64): boolean {
  if (!e.alive || e.isAir || !sim.targetable(e)) return false;
  const dx = e.x.sub(fx), dy = e.y.sub(fy);
  return dx.mul(dx).add(dy.mul(dy)).le(r2);
}
